import { useMemo } from "react"
import { type Query as CubeQuery } from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import type { DailyPerformance } from "../components/performance-utils"
import { addDays } from "./useBudgetRecommendations"

const CUBE_BY_ENTITY = {
  campaign: {
    cube: "company_media_metrics_campaigns",
    idKey: "campaign_id",
    maxDateDim:
      "company_last_updated_date.brand_media_paid_optimization_campaigns_max_date",
  },
  adset: {
    cube: "company_media_metrics_adsets",
    idKey: "adset_id",
    maxDateDim:
      "company_last_updated_date.brand_media_paid_optimization_adsets_max_date",
  },
} as const

export type PerformanceEntityKind = keyof typeof CUBE_BY_ENTITY

/**
 * Ledger platform slug → the cube `media_channel` values that belong to it.
 * External ids are only unique per platform (a Meta and a Google campaign
 * can share a numeric id), so the query must also pin the channel. Google
 * spans two slices — pipeline-dbt splits each Google campaign into a
 * youtube_ads part and a google_ads residual under the SAME campaign id —
 * and both are summed, exactly as the recommender re-merges them.
 */
const CUBE_CHANNELS_BY_PLATFORM: Record<string, string[]> = {
  meta: ["meta", "facebook", "instagram"],
  tiktok: ["tiktok"],
  pinterest: ["pinterest"],
  google: ["google_ads", "youtube", "youtube_ads"],
}

/**
 * One entity's daily spend / modeled revenue / brand value over a date
 * range, from the same paid-media cubes the Budget Recommender scores with,
 * plus the latest complete day of data (n-1 of the cube's max date — the
 * newest day is usually partial) so before/after windows never read a
 * half-day.
 */
export function useEntityPerformance(
  kind: PerformanceEntityKind,
  platform: string | null,
  entityId: string | null,
  range: { from: Date; to: Date },
) {
  const currentCompany = useCurrentCompany()
  const companyId = currentCompany?.id
  const { cube, idKey, maxDateDim } = CUBE_BY_ENTITY[kind]
  const dateRange = useMemo<[string, string]>(
    () => [
      range.from.toISOString().split("T")[0],
      range.to.toISOString().split("T")[0],
    ],
    [range],
  )

  const channels = platform
    ? CUBE_CHANNELS_BY_PLATFORM[platform.toLowerCase()]
    : undefined

  const query = useMemo<CubeQuery>(
    () => ({
      measures: [
        `${cube}.spend`,
        `${cube}.modeled_revenue`,
        `${cube}.brand_value_agg`,
      ],
      filters: [
        {
          member: `${cube}.companyid`,
          operator: "equals",
          values: [companyId ?? ""],
        },
        {
          member: `${cube}.${idKey}`,
          operator: "equals",
          values: [entityId ?? ""],
        },
        // Unknown platform slug → no channel filter rather than no data.
        ...(channels
          ? [
              {
                member: `${cube}.media_channel`,
                operator: "equals" as const,
                values: channels,
              },
            ]
          : []),
      ],
      timeDimensions: [
        { dimension: `${cube}.md_date`, granularity: "day", dateRange },
      ],
      limit: 5000,
    }),
    [cube, idKey, companyId, entityId, channels, dateRange],
  )
  const { resultSet, isLoading, error } = useCubeQuery(query, {
    skip: !companyId || !entityId,
  })

  const maxDateQuery = useMemo<CubeQuery>(
    () => ({ dimensions: [maxDateDim], limit: 1 }),
    [maxDateDim],
  )
  const { resultSet: maxDateResult } = useCubeQuery(maxDateQuery, {
    skip: !companyId,
  })

  const days = useMemo<DailyPerformance[]>(() => {
    if (!resultSet) return []
    return resultSet
      .tablePivot()
      .map(row => ({
        date: String(
          row[`${cube}.md_date.day`] ?? row[`${cube}.md_date`] ?? "",
        ).split("T")[0],
        spend: Number(row[`${cube}.spend`]) || 0,
        modeledRevenue: Number(row[`${cube}.modeled_revenue`]) || 0,
        brandValue: Number(row[`${cube}.brand_value_agg`]) || 0,
      }))
      .filter(d => d.date)
      .sort((a, b) => a.date.localeCompare(b.date))
  }, [resultSet, cube])

  const latestCompleteDate = useMemo<string | null>(() => {
    const raw = String(maxDateResult?.tablePivot()[0]?.[maxDateDim] ?? "")
    return raw ? addDays(raw.split("T")[0], -1) : null
  }, [maxDateResult, maxDateDim])

  return { days, latestCompleteDate, isLoading, error }
}
